import type { HTMLAttributes } from "react";
import type { ActivePalette } from "../../utils/activePalette/types";
import type { BlockVisibility } from "../common/BlockVisibility";
import type { DynamicStructure } from "../../utils/interpolate/types";
import type { ContentFieldTypeValue } from "../../types/cms/contentField.type";

/** Mesma forma que as outras seções usam (não há tipo compartilhado no pacote). */
export type PaddingProps = {
  typeMeasure?: string;
  desktop?: { paddingTop?: string; paddingBottom?: string; top?: string; bottom?: string };
  mobile?: { paddingTop?: string; paddingBottom?: string; top?: string; bottom?: string };
};

/**
 * Contrato do bloco "45 - Destaques Dinâmicos".
 *
 * O bloco é Individual: lê o registro da página de item e resolve, PELO TIPO
 * (`highlights`), a lista de destaques cadastrada nele. Não há mapeamento de
 * campos no sidebar — o único caso em que o Champion escolhe algo é quando a
 * coleção tem mais de um campo do tipo (`highlightsField`).
 *
 * As props são planas de propósito (é o formato que o Craft serializa e que os
 * outros blocos usam); os agrupamentos abaixo são só de leitura.
 */

/** Um item do campo `highlights`, como o CMS entrega. */
export interface HighlightItem {
  id: string;
  /** URL da imagem (mesmo formato do tipo `image`). */
  image: string;
  title: string;
  description: string;
  /** HTML (rich text). Vazio = o item não abre modal. */
  extendedDescription?: string;
}

/**
 * `overlay` = texto sobre a imagem; `below` = card clássico (fundo + borda) com
 * o texto abaixo; `bare` = texto abaixo SEM moldura de card (só imagem e texto).
 */
export type HighlightsFormat = "overlay" | "below" | "bare";
export type HighlightsPresentation = "carousel" | "grid";
/** Grade regular (todas as células iguais) ou bento (composição por quantidade). */
export type HighlightsGridArrangement = "uniform" | "bento";
export type HighlightsNavigation = "arrows" | "indicator" | "arrowsAndIndicator" | "none";
/**
 * Tipo de seta da navegação.
 *
 * - `outline` / `filled`: chevron dentro de um disco (com borda ou preenchido)
 * - `chevronOnly`: só o chevron, sem disco — área justa ao glifo
 * - `arrowShort`: seta convencional, sem disco
 * - `arrow`: a mesma seta, alongada (haste de borda a borda)
 */
export type HighlightsArrowType =
  | "outline"
  | "filled"
  | "chevronOnly"
  | "arrowShort"
  | "arrow";
/** Tipo de indicador do carrossel: barra, círculos, traços, numerado. */
export type HighlightsIndicatorType = "bar" | "pill" | "lines" | "counter";
/** Largura do trilho do carrossel — mesmos valores do bloco 30 (Galeria). */
export type HighlightsCarouselWidth = "content" | "full";
/** Proporção da imagem do card — mesmo par do `ImageAspectRatioControl`. */
export type HighlightsImageAspect = "fixed" | "auto";
/** Altura do card: quão alto ele é em relação à largura. */
export type HighlightsCardHeight = "low" | "medium" | "tall";
/** Alinhamento do cabeçalho. */
export type HighlightsHeaderAlign = "start" | "center";
/** Valores por device — mesmo formato do `ResponsiveTextAlign` do MediaSectionV2. */
export type ResponsiveHeaderAlign = { desktop: HighlightsHeaderAlign; mobile: HighlightsHeaderAlign };
export type ResponsiveCardHeight = { desktop: HighlightsCardHeight; mobile: HighlightsCardHeight };
/** Estilo do botão de detalhes no card — mesmo vocabulário do `BlockButtonsEditor`. */
export type HighlightsDetailsVariant = "link" | "outline" | "filled";
/** `0` = todos. */
export type HighlightsMaxItems = 0 | 4 | 6 | 8 | 12;

/** Tag HTML do texto de um card — mesmo vocabulário do `TagSelector` do builder. */
export type HighlightsTextTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";

export interface DynamicHighlightsProps
  extends HTMLAttributes<HTMLDivElement>,
    ActivePalette,
    BlockVisibility {
  htmlId?: string;
  padding?: PaddingProps;
  preload?: boolean;

  // ---- Cabeçalho do bloco (InputCMSText, context item) ----
  title?: string;
  titleTag?: string;
  titleVisible?: boolean;
  description?: string;
  descriptionVisible?: boolean;
  /**
   * Alinhamento do cabeçalho, por device.
   *
   * No desktop o valor decide TAMBÉM onde ficam as setas (na linha do título
   * quando à esquerda, no rodapé quando centralizado). No mobile elas estão
   * sempre no rodapé, então ali o alinhamento vale para o texto do cabeçalho e
   * para a posição horizontal de setas e indicador.
   */
  headerAlign?: ResponsiveHeaderAlign;

  // ---- Conteúdo dinâmico ----
  /** Nome do campo `repeater` a usar. Vazio = o único do tipo. */
  highlightsField?: string;
  /** De qual sub-campo vem cada parte do card. */
  fieldMap?: HighlightsFieldMap;
  /** Mostra o título no card. */
  showTitle?: boolean;
  /** Mostra a descrição curta no card. */
  showDescription?: boolean;
  /**
   * Tag do título do CARD — `h3` por default, um nível abaixo do `h2` do
   * cabeçalho da seção, para a página manter a hierarquia de headings.
   */
  itemTitleTag?: HighlightsTextTag;
  /** Tag da descrição curta do card. */
  itemDescriptionTag?: HighlightsTextTag;
  /** Rótulo do link de detalhes no card ("Ver mais"). */
  detailsLabel?: string;
  /** Estilo do botão de detalhes: link sublinhado, contorno ou sólido. */
  detailsVariant?: HighlightsDetailsVariant;

  // ---- Modal ----
  modalEnabled?: boolean;
  modalBgColor?: string;
  modalTitleColor?: string;
  modalTextColor?: string;
  /** Cor do X, das setas e do indicador da janela. Vazio = cor atual (X branco, setas/indicador na cor da navegação). */
  modalIconColor?: string;

  // ---- Design ----
  backgroundType?: "color" | "image";
  bgColor?: string;
  backgroundImage?: {
    desktop?: { props: { src?: string; alt?: string; width?: number; height?: number } };
    mobile?: { props: { src?: string; alt?: string; width?: number; height?: number } };
  };
  /** Só no formato `below`. */
  cardBgColor?: string;
  cardBorderColor?: string;
  titleColor?: string;
  descriptionColor?: string;
  itemTitleColor?: string;
  itemDescriptionColor?: string;
  detailsLinkColor?: string;
  /** Só no formato `overlay`: cor com alfa (hex8) da base do gradiente. */
  overlayColor?: string;
  navigationColor?: string;

  // ---- Layout ----
  format?: HighlightsFormat;
  presentation?: HighlightsPresentation;
  gridArrangement?: HighlightsGridArrangement;
  /** Itens por linha (grade regular) — 2..4. */
  gridColumns?: number;
  /** Itens por página (carrossel) — 1..3. */
  carouselItemsPerPage?: number;
  maxItems?: HighlightsMaxItems;
  navigation?: HighlightsNavigation;
  arrowType?: HighlightsArrowType;
  indicatorType?: HighlightsIndicatorType;
  /** Repetição infinita — ignorada quando há espia (padrão), ver skill §7.6. */
  carouselLoop?: boolean;
  /** Reprodução automática do carrossel (padrão do carrossel; nasce desligada). */
  carouselAutoplay?: boolean;
  /** Intervalo do autoplay em ms. Só vale com `carouselAutoplay`. */
  carouselAutoplaySpeed?: number;
  /**
   * Largura do trilho do carrossel. `content` para na coluna de conteúdo;
   * `full` vaza pela direita da viewport (Porsche `extended`).
   */
  carouselWidth?: HighlightsCarouselWidth;
  /** Proporção da imagem do card: `fixed` usa a proporção do formato, `auto` respeita a original. */
  imageAspect?: HighlightsImageAspect;
  /** Altura do card, por device. Inerte quando `imageAspect` é `auto`. */
  cardHeight?: ResponsiveCardHeight;
  /** Grade/bento viram carrossel no mobile. */
  gridShowAsCarouselOnMobile?: boolean;

  // ---- Animação (transversal) ----
  animationEnabled?: boolean;
  animationEffect?: string;
  animationType?: string;
  animationSpeed?: string;
  animationDevices?: string;

  // ---- Runtime ----
  /** Registro da página (`item`) — entregue pelo Node.tsx / wrapper do builder. */
  dynamicStructure?: DynamicStructure;
  /** Injetado pelo wrapper da ui; nunca serializado. Sem itens: builder orienta, publicado some. */
  isBuilder?: boolean;
  /**
   * Só o CANVAS de edição (não a previsualização, que também é builder).
   * Desliga interações que competem com a seleção do bloco — hoje, abrir o
   * modal. Injetado pelo `*Wrapper` da ui; o `*ReadOnly` nunca passa.
   */
  isEditing?: boolean;
  /** Render function para a imagem (next/image no renderer, <img> no builder/story). */
  renderImage?: (props: { src: string; alt: string; width?: number; height?: number; className?: string }) => JSX.Element;
}

export const REPEATER_FIELD_TYPE: ContentFieldTypeValue = "repeater";

/**
 * Qual sub-campo do repetidor alimenta cada parte do card.
 *
 * O repetidor não tem forma fixa — os sub-campos são definidos pelo racer —,
 * então o bloco precisa saber de onde vem cada coisa. Vazio em `image` ou
 * `title` faz o item ser descartado; vazio em `extendedDescription` faz o card
 * não abrir modal.
 */
export type HighlightsFieldMap = {
  image?: string;
  title?: string;
  description?: string;
  extendedDescription?: string;
};

/**
 * Proporção do card por formato e viewport.
 *
 * Cada device tem sua PRÓPRIA escala, e a do mobile é inteira mais alta.
 *
 * O motivo é a largura da célula: no mobile é sempre uma coluna, então o card
 * ocupa a tela toda (~390px) e a mesma proporção do desktop achataria a
 * imagem — 16/9 em 390px dá 219px, onde a foto fica ilegível. Por isso o passo
 * mobile começa onde o desktop termina: `low` no mobile é a `medium` do
 * desktop, e assim por diante.
 *
 * (Antes o mobile era um valor fixo e o seletor de altura simplesmente não
 * valia lá — um controle que não fazia nada em metade dos dispositivos.)
 */
export const HIGHLIGHT_ASPECT = {
  overlay: {
    desktop: { low: "16 / 9", medium: "4 / 3", tall: "2 / 3" },
    mobile: { low: "4 / 3", medium: "1 / 1", tall: "2 / 3" },
  },
  below: {
    desktop: { low: "16 / 9", medium: "4 / 3", tall: "3 / 4" },
    mobile: { low: "3 / 2", medium: "4 / 3", tall: "3 / 4" },
  },
  bare: {
    desktop: { low: "16 / 9", medium: "4 / 3", tall: "3 / 4" },
    mobile: { low: "3 / 2", medium: "4 / 3", tall: "3 / 4" },
  },
} as const;

/**
 * Proporção do card quando há UM item por página no carrossel.
 *
 * O slide ocupa a coluna inteira (~1100px), e aí a proporção do card normal —
 * que é de retrato — daria 839px de altura na opção "média" e 1678px na
 * "alta". A escala aqui é panorâmica, na mesma lógica das células largas do
 * bento: card largo pede proporção larga.
 */
export const HIGHLIGHT_SINGLE_ASPECT = {
  low: "21 / 9",
  medium: "2 / 1",
  tall: "16 / 9",
} as const;

/**
 * Proporção das células LARGAS do bento.
 *
 * Não é uma escala escolhida a olho: a célula larga divide a LINHA com a
 * estreita, e o grid iguala as duas pela mais alta. Para a imagem da larga
 * preencher a célula (em vez de sobrar faixa vazia), a proporção precisa
 * render a MESMA altura que a estreita ao lado.
 *
 * Numa coluna de 1216px com 3 colunas e gap de 24px, a larga mede 803px e a
 * estreita 389px — ou seja, a larga é ~2,06× mais larga, e cada proporção aqui
 * é a do card normal multiplicada por esse fator:
 *
 *   low     16/9 →  219px de altura  → 803/219 ≈ 3,67  → 33/9
 *   medium   4/3 →  292px            → 803/292 ≈ 2,75  → 11/4
 *   tall     2/3 →  584px            → 803/584 ≈ 1,38  → 11/8
 *
 * Mexeu na escala do card normal? Recalcule esta. O teste `aspectScales`
 * guarda o pareamento.
 */
export const HIGHLIGHT_WIDE_ASPECT = {
  low: "33 / 9",
  medium: "11 / 4",
  tall: "11 / 8",
} as const;

/**
 * Teto de altura do card. A opção "alta" é um retrato (2/3), e num slide largo
 * isso passava de 1600px — quase duas telas. O teto corta o exagero sem mexer
 * na proporção das outras alturas, que ficam bem abaixo dele.
 */
export const HIGHLIGHT_MAX_HEIGHT = "70vh";

/**
 * Espaçamento entre itens (grade e carrossel), em px.
 *
 * FIXO por device, sem controle na sidebar: o valor certo é uma decisão de
 * design system, não de bloco — um gap escolhido a dedo por página é a origem
 * de ritmos diferentes entre blocos da mesma tela. No mobile é menor porque a
 * coluna única já separa os cards por si.
 */
export const ITEM_GAP = { desktop: 24, mobile: 16 } as const;

/** Espia do próximo card no carrossel (fração da largura do trilho). */
export const HIGHLIGHT_PEEK = 0.08;

/** Intervalo default do autoplay, alinhado ao bloco 28. */
export const DEFAULT_AUTOPLAY_SPEED = 3000;
